import type { Board } from "../board";
import type { DamageType } from "../damageType";
import type { PostDeserializable } from "../interfaces";
import { deserializeXY, serializeXY } from "../serialization";
import type { Thing } from "../thing";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { XY } from "../xy";
import { HeavyRobot } from "./heavyRobot";

export class HeavyRobotPart extends Mob implements PostDeserializable {
	static key = "HeavyRobotPart";
	private master!: HeavyRobot;
	private _pendingMasterXY: { x: number; y: number } | null = null;
	masterSetCoords: XY | null = null;

	constructor(xy: XY) {
		super(xy);
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	setMaster(master: HeavyRobot): void {
		this.master = master;
	}

	getMaster(): HeavyRobot {
		return this.master;
	}

	processMobAction(_board: Board): void {
		if (this.masterSetCoords) {
			this.xy = this.masterSetCoords;
			this.masterSetCoords = null;
		}
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		// Proxy damage to master unless the source IS the master (onZeroHp chain)
		if (this.master && !(source instanceof HeavyRobot)) {
			return this.master.applyDamage(type, amount, source);
		}
		return super.applyDamage(type, amount, source);
	}

	override onProjectileHit(projectile: unknown): void {
		if (this.master) {
			this.master.onProjectileHit(projectile);
		}
	}

	override isVisible(): boolean {
		return true;
	}

	override getZOrder(): number {
		return this.master ? this.master.getZOrder() : super.getZOrder();
	}

	override isHideUnderneath(): boolean {
		return this.master ? this.master.isHideUnderneath() : false;
	}

	override getProcessPriority(): number {
		return this.master ? this.master.getProcessPriority() + 1 : super.getProcessPriority() + 1;
	}

	postDeserialize(board: Board): void {
		if (!this._pendingMasterXY) return;
		const masterXY = new XY(this._pendingMasterXY.x, this._pendingMasterXY.y);
		const things = board.getThingsAt(masterXY);
		const master = things.find((t) => t instanceof HeavyRobot);
		if (master) {
			this.setMaster(master as HeavyRobot);
			(master as HeavyRobot).addPart(this);
		}
		this._pendingMasterXY = null;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			masterXY: this.master ? serializeXY(this.master.xy) : null,
		};
	}

	static deserialize(data: any): HeavyRobotPart {
		const p = new HeavyRobotPart(deserializeXY(data.xy));
		p.restoreMobFields(data);
		if (data.masterXY) {
			p._pendingMasterXY = data.masterXY;
		}
		return p;
	}
}
